import {
  Button,
  Card,
  Checkbox,
  Group,
  Tooltip as MantineTooltip,
  Stack,
  TextInput,
  Textarea
} from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  LabelAutocomplete,
  LabelOption
} from '@/components/labels/LabelAutocomplete/LabelAutocomplete'
import {
  Dataset,
  DatasetType,
  DatasetVersion,
  LabelingType
} from '@/types/dataset'
import { Label } from '@/types/label'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import {
  DatasetSelectHandler,
  DatasetSelectionValues
} from '../DatasetSelect/DatasetSelectHandler'
import { DatasetTypeSelect } from './DatasetTypeSelect'
import { LabelingTypeSelect } from './LabelingTypeSelect'

export const CLONE_PREFIX = 'CLONE - '
const DESCRIPTION_MAX_LENGTH = 500

type TooltipProps = {
  label: ReactNode
  children: ReactNode
  disabled?: boolean
}

const Tooltip = ({ label, children, disabled }: TooltipProps) => {
  return (
    <MantineTooltip
      disabled={disabled}
      label={label}
      position="bottom-start"
      w={320}
      offset={{
        mainAxis: 10,
        crossAxis: 20
      }}
      multiline
    >
      {children}
    </MantineTooltip>
  )
}

export type FormValues = {
  name: string
  description: string
  datasetType: DatasetType
  labels: (Label | LabelOption)[] // it is possible to select an existing label or create a new one (without an id), LabelOption is a subset of Label
  labelingType: LabelingType
  shouldCloneDataset: boolean
  originalDataset: Dataset | null
  originalDatasetVersion: DatasetVersion | null
  includeAnnotations: boolean
  deleteOriginal: boolean
}

type InternalFormValues = Omit<FormValues, 'datasetType' | 'labelingType'> & {
  datasetType: string
  labelingType: string
}

type DatasetFormProps = {
  initialValues?: Partial<FormValues>
  submitLabel: string | ReactNode
  isAnnotated?: boolean
  bottomContent?: ReactNode
  withClone?: boolean
  isDatasetSelectDisabled?: boolean
  onCancel: () => void
  onSubmit: (formValues: FormValues) => void
}

export const DatasetForm = ({
  initialValues,
  submitLabel,
  isAnnotated,
  bottomContent,
  withClone,
  isDatasetSelectDisabled,
  onCancel,
  onSubmit
}: DatasetFormProps) => {
  const intl = useIntl()

  const form = useForm<InternalFormValues>({
    initialValues: {
      name: initialValues?.name || '',
      description: initialValues?.description || '',
      datasetType: String(initialValues?.datasetType || DatasetType.Primary),
      labels: initialValues?.labels || [],
      labelingType: String(
        initialValues?.labelingType || LabelingType.ObjectDetection
      ),
      shouldCloneDataset: initialValues?.shouldCloneDataset || false,
      originalDataset: initialValues?.originalDataset || null,
      originalDatasetVersion: initialValues?.originalDatasetVersion || null,
      includeAnnotations: initialValues?.includeAnnotations || true,
      deleteOriginal: initialValues?.deleteOriginal || false
    },

    validate: {
      name: isNotEmpty(
        intl.formatMessage({
          id: 'datasets.name.required'
        })
      ),
      description: (value) => {
        if (value.length > DESCRIPTION_MAX_LENGTH) {
          return intl.formatMessage(
            {
              id: 'datasets.description.maxChars'
            },
            {
              maxChars: DESCRIPTION_MAX_LENGTH
            }
          )
        }
      },
      datasetType: (value, values) => {
        if (!value) {
          return intl.formatMessage({
            id: 'datasets.type.required'
          })
        }

        // Primary datasets cannot be used for classification
        if (
          value === String(DatasetType.Primary) &&
          values.labelingType === String(LabelingType.Classification)
        ) {
          return intl.formatMessage({
            id: 'datasets.type.classificationSecondaryOnly'
          })
        }
      },
      labelingType: isNotEmpty(
        intl.formatMessage({
          id: 'annotation.type.required'
        })
      ),
      labels: (value) => {
        if (value.length === 0) {
          return intl.formatMessage({
            id: 'annotation.classes.required'
          })
        }
      },
      originalDatasetVersion: (value, values) => {
        if (values.shouldCloneDataset && !value) {
          return intl.formatMessage({
            id: 'datasets.clone.select.required'
          })
        }
      },
      includeAnnotations: (value, values) => {
        if (values.shouldCloneDataset && value) {
          const originalDatasetLabels = values.originalDataset?.labels || []

          // check if has common labels
          if (
            !originalDatasetLabels.some((originalLabel) =>
              values.labels.some((label) => label.id === originalLabel.id)
            )
          ) {
            return intl.formatMessage({
              id: 'datasets.clone.copyAnnotations.notAllowed'
            })
          }
        }

        return null
      }
    }
  })

  const handleSubmit = (values: InternalFormValues) => {
    onSubmit({
      ...values,
      // Mantine select component supports only string values, so we need to convert them back to numbers
      datasetType: Number(values.datasetType),
      labelingType: Number(values.labelingType)
    })
  }

  const handleOriginalDatasetChange = (
    selectionValues: DatasetSelectionValues
  ) => {
    const { dataset, version } = selectionValues

    form.setFieldValue('originalDataset', dataset || null)
    form.setFieldValue('originalDatasetVersion', version || null)

    // Prefill values from the selected dataset for cloning
    if (
      (!form.getValues().name ||
        form.getValues().name.startsWith(CLONE_PREFIX)) &&
      dataset
    ) {
      form.setFieldValue('name', `${CLONE_PREFIX}${dataset.name}`)
    }

    form.setFieldValue(
      'datasetType',
      String(dataset?.dataset_type || DatasetType.Primary)
    )

    form.setFieldValue(
      'labelingType',
      String(dataset?.labeling_type || LabelingType.ObjectDetection)
    )

    form.setFieldValue('labels', dataset?.labels || [])
  }

  return (
    <form noValidate onSubmit={form.onSubmit((values) => handleSubmit(values))}>
      <Stack gap="xl">
        {withClone && (
          <Card bg="gray.1" padding="lg">
            <Checkbox
              disabled={isDatasetSelectDisabled}
              label={<FormattedMessage id="datasets.clone.checkbox.label" />}
              {...form.getInputProps('shouldCloneDataset', {
                type: 'checkbox'
              })}
            />

            {form.getValues().shouldCloneDataset && (
              <DatasetSelectHandler
                initialSelectionState={{
                  datasetId: initialValues?.originalDataset?.id || null,
                  versionId: initialValues?.originalDatasetVersion?.id || null
                }}
                listParams={{
                  is_library: false,
                  ordering: 'name',
                  limit: MAX_ITEMS_WITHOUT_PAGINATION
                }}
                inputProps={{
                  mt: 'lg',
                  error: form.errors.originalDatasetVersion,
                  disabled: isDatasetSelectDisabled
                }}
                preselectFirst
                onChange={handleOriginalDatasetChange}
              />
            )}
          </Card>
        )}

        <TextInput
          label={<FormattedMessage id="datasets.name" />}
          autoFocus
          data-autofocus // required for auto focus inside the modal
          {...form.getInputProps('name')}
        />

        <Textarea
          label={<FormattedMessage id="datasets.description" />}
          minRows={4}
          maxRows={4}
          autosize
          {...form.getInputProps('description')}
        />

        <DatasetTypeSelect
          {...form.getInputProps('datasetType')}
          disabled={form.getValues().shouldCloneDataset}
        />

        <Tooltip
          disabled={!isAnnotated}
          label={
            <FormattedMessage id="annotation.classes.lockedAfterAnnotation" />
          }
        >
          <LabelingTypeSelect
            disabled={isAnnotated || form.getValues().shouldCloneDataset}
            {...form.getInputProps('labelingType')}
          />
        </Tooltip>

        <Tooltip
          disabled={!isAnnotated}
          label={
            <FormattedMessage id="annotation.classes.lockedAfterAnnotation" />
          }
        >
          <LabelAutocomplete
            label={<FormattedMessage id="annotation.classes" />}
            values={form.getValues().labels}
            error={form.errors.labels}
            disabled={isAnnotated}
            onChange={(value) => form.setFieldValue('labels', value)}
          />
        </Tooltip>

        {withClone && form.getValues().shouldCloneDataset && (
          <Card bg="gray.1" padding="lg">
            <Checkbox
              mb="md"
              label={
                <FormattedMessage id="datasets.clone.copyAnnotations.label" />
              }
              description={
                <FormattedMessage id="datasets.clone.copyAnnotations.description" />
              }
              {...form.getInputProps('includeAnnotations', {
                type: 'checkbox'
              })}
            />

            <Checkbox
              label={
                <FormattedMessage id="datasets.clone.deleteOriginal.label" />
              }
              description={
                <FormattedMessage id="datasets.clone.deleteOriginal.description" />
              }
              {...form.getInputProps('deleteOriginal', {
                type: 'checkbox'
              })}
            />
          </Card>
        )}

        {bottomContent}
      </Stack>

      <Group justify="end" mt={48}>
        <Button miw={120} variant="default" onClick={onCancel}>
          <FormattedMessage id="cancel" />
        </Button>

        <Button type="submit" miw={120}>
          {submitLabel}
        </Button>
      </Group>
    </form>
  )
}
